import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getDailySeed, hashCode } from '../utils/formatting';

export function useFeaturedPlaylists(limit = 4, genre?: string, subGenre?: string) {
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Add effect for prerenderReady
  useEffect(() => {
    if (!loading && playlists.length > 0) {
      // All critical data has loaded, tell prerender service we're ready
      if (typeof window !== 'undefined' && window.prerenderReady !== undefined) {
        window.prerenderReady = true;
      }
    }
  }, [loading, playlists.length]);

  useEffect(() => {
    async function fetchFeaturedPlaylists() {
      try {
        setLoading(true);
        setError(null);

        // First, fetch featured playlists
        let featuredQuery = supabase
          .from('playlist_details')
          .select('*')
          .eq('featured', true);

        if (genre) {
          featuredQuery = featuredQuery.eq('genre', genre);
        }

        if (subGenre) {
          featuredQuery = featuredQuery.contains('sub_genres', [subGenre]);
        }

        const { data: featured, error: featuredError } = await featuredQuery;

        if (featuredError) throw featuredError;

        // Always prioritize featured playlists, even if we don't have enough
        const featuredPlaylists = featured || [];
        
        // If we have enough featured playlists, just use them
        if (featuredPlaylists.length >= limit) {
          const seed = getDailySeed();
          const shuffled = [...featuredPlaylists].sort((a, b) => {
            const hashA = hashCode(`${seed}-${a.id}`);
            const hashB = hashCode(`${seed}-${b.id}`);
            return hashA - hashB;
          });
          setPlaylists(shuffled.slice(0, limit));
          return;
        }

        // If we don't have enough featured playlists, we need to fetch additional non-featured ones
        const needed = limit - featuredPlaylists.length;
        let nonFeaturedQuery = supabase
          .from('playlist_details')
          .select('*')
          .eq('featured', false);

        if (genre) {
          nonFeaturedQuery = nonFeaturedQuery.eq('genre', genre);
        }

        if (subGenre) {
          nonFeaturedQuery = nonFeaturedQuery.contains('sub_genres', [subGenre]);
        }

        const { data: nonFeatured, error: nonFeaturedError } = await nonFeaturedQuery;

        if (nonFeaturedError) throw nonFeaturedError;

        // Use the daily seed for deterministic shuffling
        const seed = getDailySeed();
        
        // Shuffle featured playlists
        const shuffledFeatured = [...featuredPlaylists].sort((a, b) => {
          const hashA = hashCode(`${seed}-featured-${a.id}`);
          const hashB = hashCode(`${seed}-featured-${b.id}`);
          return hashA - hashB;
        });
        
        // Shuffle non-featured playlists
        const shuffledNonFeatured = [...(nonFeatured || [])].sort((a, b) => {
          const hashA = hashCode(`${seed}-nonfeatured-${a.id}`);
          const hashB = hashCode(`${seed}-nonfeatured-${b.id}`);
          return hashA - hashB;
        });
        
        // Combine: featured first, then non-featured to fill up to limit
        const finalPlaylists = [
          ...shuffledFeatured,
          ...shuffledNonFeatured.slice(0, needed)
        ];

        setPlaylists(finalPlaylists);
      } catch (err) {
        console.error('Failed to fetch playlists:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch playlists'));
      } finally {
        setLoading(false);
      }
    }

    fetchFeaturedPlaylists();
  }, [limit, genre, subGenre]);

  return { playlists, loading, error };
} 